import React, { useState, useRef, useEffect, Component, Fragment } from 'react';
import { Provider } from 'react-redux';
import 'babel-polyfill';
import PropTypes from 'prop-types';
import cn from 'classnames';
import DesignStatus from '../../common/components/DesignStatus/DesignStatus';
import shoppingInfoFormUrl from '../../common/utils/shoppingInfoForm';
import createStore from '../../customer_chat/store/createStore';
import Chat from '../../customer_chat/containers/ChatContainer';
import SvgIcon from '../../common/SvgIcon';
import DiscountVouchersDialog from '../../design/routes/Moodboard/components/DiscountVouchersDialog';
import CustomerBasketDialog from '../../design/routes/Moodboard/components/CustomerBasketDialog';
import request from '../../common/utils/request';
import { MOOD_PLACEHOLDER } from '../../design/routes/Moodboard/modules/moodboard-drag-and-drop';
import { isConsultancy } from '../customer/brief/utils/projectStatusForCustomer';
import workInProgressImg from "../customer_projects_list/work_in_progress.jpg";

const EMPTY_SHOPPING = {
  credit_total_text: null,
  favourites_count: 0,
  orders_count: 0,
  items_count: 0,
  last_order_number: null,
  orders_url: null,
};

// What an empty list says depends on which one you are looking at.
const EMPTY_COPY = {
  pinned: 'You have no pinned designs.',
  active: 'You have no designs yet.',
  paused: 'You have no paused designs.',
  archived: 'You have no archived designs.',
};

// The customer search box lives in the page header (my_projects.slim), outside
// this component, as a plain GET form; the component takes it over from here.
const SEARCH_FORM_ID = 'designer_projects_search';
const SEARCH_DELAY_MS = 500;

export default function DesignerProjectsList({
  projectsByCustomer: projectsByCustomerInitial,
  filter,
  query: queryInitial,
  loadMoreLink,
  shoppingFormUser,
}) {
  const [projectsByCustomer, setProjectsByCustomer] = useState(projectsByCustomerInitial);
  const [nextLink, setNextLink] = useState(loadMoreLink);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState(queryInitial);
  // Bumped on every search, so an answer that arrives after a newer search was
  // sent - or a Load more that started before it - is dropped, not shown.
  const searchSeq = useRef(0);
  const [pinningIds, setPinningIds] = useState([]);
  const [collapsedIds, setCollapsedIds] = useState([]);
  // The Shopping Credits table the project page opens from its header, shown
  // here from a customer's credit line.
  const [vouchers, setVouchers] = useState({ visible: false, loading: false, data: [] });
  // The same goes for the customer's basket, opened from the shopping bag.
  const [basket, setBasket] = useState({ visible: false, loading: false, data: [], errors: null });
  // Items in each customer's basket, by customer id. Fetched after the cards
  // are on screen - a cart lookup per customer is too slow to hold the page
  // for - and only for customers not asked about yet.
  const [basketCounts, setBasketCounts] = useState({});
  const basketCountsAsked = useRef({});
  // Kept per customer for the life of the component. <Provider> refuses to swap
  // a store once mounted, so building one inside render - which now happens on
  // every pin click and every Load more - would log an error and leak a store
  // per render.
  const chatStores = useRef({});
  function getChatStore(chat, customerId) {
    if (!chatStores.current[customerId]) {
      chatStores.current[customerId] = createStore({
        chat: {
          messages: [],
          message: {
            images: [],
            files: [],
          },
          ...chat,
        },
      });
    }
    return chatStores.current[customerId];
  }

  const projectApproved = project => project.status === 'approved';

  const projectMoreTimeApproved = project => project.additional_hours_status === 'approved';

  const projectArchivedForCustomer = project => project.status === 'archived';

  const categoryReleasedForCustomer = project =>
    project.category_release_status === 'approved' && project.status === 'in_progress';

  const projectInProgress = project =>
    project.status === 'in_progress' || project.status === 'awaiting';

  const projectOnboarding = project => project.status === 'onboarding';

  const projectOnPause = project => project.status === 'on_pause';

  const renderImage = project => (
    <div className="image -fourxthree">
      {project.images[0] && <img className="image_body" src={project.images[0].thumb} alt="" />}
    </div>
  );

  const renderProjectCompleted = project => renderImage(project);

  const renderProjectOnPause = project => {
    if (project.images.length > 0) {
      return renderImage(project);
    }

    return (
      <div className="text-with-icon">
        <div className="text-with-icon_inner">
          <div className="status-icon -on-pause">
            <svg className="">
              <use xlinkHref="#project-on-pause" xmlnsXlink="http://www.w3.org/1999/xlink" />
            </svg>
          </div>
        </div>
      </div>
    );
  };

  const renderProjectInProgress = project => {
    if (project.images.length > 0) {
      return renderImage(project);
    }
    return (
      <div className="image -fourxthree">
        <img className="image_body" src={workInProgressImg} alt=""/>
      </div>
    );
  };

  const renderCategoryReleasedForCustomer = project => {
    if (project.images.length > 0) {
      return renderImage(project);
    }
    return (
      <div className="image -fourxthree">
        <img className="image_body" src={workInProgressImg} alt=""/>
      </div>
    );
  };

  const renderProjectOnboarding = () => (
    <div className="text-with-icon">
      <div className="text-with-icon_inner">
        <div className="status-icon">
          <svg className="">
            <use xlinkHref="#project-onboarding" xmlnsXlink="http://www.w3.org/1999/xlink" />
          </svg>
        </div>
      </div>
    </div>
  );

  const renderProjectImage = project => {
    if (projectOnPause(project)) {
      return renderProjectOnPause(project);
    }
    if (projectApproved(project)) {
      return renderProjectCompleted(project);
    }
    if (projectArchivedForCustomer(project)) {
      return renderProjectCompleted(project);
    }
    if (categoryReleasedForCustomer(project)) {
      return renderCategoryReleasedForCustomer(project);
    }
    if (projectOnboarding(project)) {
      return renderProjectOnboarding(project);
    }
    return renderProjectInProgress(project);
  };

  const renderStatusDescription = project => {
    if (projectOnboarding(project)) {
      return (
        <div className="project-preview_attention -plain">
          <div className="project-preview_attention_title">
            Stage 2 of the brief is not completed.
          </div>
        </div>
      );
    }

    if (categoryReleasedForCustomer(project)) {
      return (
        <div className="project-preview_attention -green -plain">
          <div className="project-preview_attention_title">
            Design in progress: Categories released to customer!
          </div>
        </div>
      );
    }

    if (projectArchivedForCustomer(project)) {
      // Every card on the Archived tab is archived, so it would only repeat the tab.
      if (filter === 'archived') {
        return null;
      }
      return (
        <div className="project-preview_attention -green -plain">
          <div className="project-preview_attention_title">Design archived.</div>
        </div>
      );
    }

    if (projectApproved(project)) {
      if (isConsultancy(project)) {
        return (
          <div className="project-preview_attention -green -plain">
            <div className="project-preview_attention_title">Consultancy complete.</div>
            <div>
              The customer has &nbsp;
              <b> {project.days_before_archived} days</b>
              to ask your questions before the design is archived.
            </div>
            <div className="">
              Hours bought: {project.hours_purchased}
              <br />
              Hours complete: {project.hours_used}
            </div>
          </div>
        );
      }
      return (
        <div className="project-preview_attention -green -plain">
          <div className="project-preview_attention_title">Design issued to customer.</div>
          <div>
            The customer has &nbsp;
            <b> {project.days_before_archived} days </b>
            to ask your questions before the design is archived.
          </div>
        </div>
      );
    }

    if (projectInProgress(project)) {
      if (projectMoreTimeApproved(project)) {
        return (
          <div className="project-preview_attention -red -plain">
            <div className="project-preview_attention_title">
              <SvgIcon icon="icon-more-hours" />
              {isConsultancy(project)
                ? 'More Consultancy hours added.'
                : 'Design issued but more hours added.'}
            </div>
            <div> Let head off know when you're done and we will archive the project. </div>
          </div>
        );
      }

      // the timeline hint says it instead (renderTimelineHint)
      return null;
    }

    return null;
  };

  // Same branch renderStatusDescription leaves empty: the plain in-progress
  // project whose timeline is editable.
  const renderTimelineHint = project => {
    if (
      !projectInProgress(project) ||
      categoryReleasedForCustomer(project) ||
      projectMoreTimeApproved(project)
    ) {
      return null;
    }
    return (
      <div className="project-preview_attention -green -plain">
        <div className="project-preview_attention_title">
          {/* A consultancy has no timeline to update. */}
          {isConsultancy(project)
            ? 'Consultancy in progress'
            : 'Design in progress: please update the timeline as you go.'}
        </div>
      </div>
    );
  };

  // Whether the customer has bought anything yet and has credit left to spend -
  // the first things a designer wants to know about them. Laid out like the
  // other stats: the basket, then what they have shopped.
  const showVouchers = url => event => {
    event.preventDefault();
    event.stopPropagation();
    setVouchers({ visible: true, loading: true, data: [] });
    request('get', url)
      .then(data => setVouchers({ visible: true, loading: false, data: data.discount_vouchers }))
      .catch(() => setVouchers({ visible: true, loading: false, data: [] }));
  };

  const closeVouchers = () => setVouchers({ visible: false, loading: false, data: [] });

  const showBasket = url => event => {
    event.preventDefault();
    event.stopPropagation();
    setBasket({ visible: true, loading: true, data: [], errors: null });
    request('get', url)
      .then(data =>
        setBasket({ visible: true, loading: false, data: data.line_items_groups, errors: null }),
      )
      .catch(error => setBasket({ visible: true, loading: false, data: [], errors: error }));
  };

  const closeBasket = () => setBasket({ visible: false, loading: false, data: [], errors: null });

  useEffect(
    () => {
      const ids = projectsByCustomer
        .map(group => group.customer.id)
        .filter(id => !basketCountsAsked.current[id]);
      if (ids.length === 0) {
        return;
      }
      ids.forEach(id => {
        basketCountsAsked.current[id] = true;
      });
      request('get', '/design/projects/basket_counts.json', { params: { customer_ids: ids } })
        .then(data => setBasketCounts(counts => ({ ...counts, ...data.basket_counts })))
        .catch(() => {
          // No number is better than a wrong one; let the next render ask again.
          ids.forEach(id => {
            delete basketCountsAsked.current[id];
          });
        });
    },
    [projectsByCustomer],
  );

  const renderShoppingStat = (shoppingRaw, basketUrl, basketCount) => {
    const shopping = shoppingRaw || EMPTY_SHOPPING;
    const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
    const shopped = shopping.orders_count > 0;

    return (
      <div className="designer-projects-customer_stat -shopping">
        {basketUrl ? (
          <a
            className="designer-projects-customer_stat_icon-slot -basket"
            href={basketUrl}
            title="See this customer's basket"
            onClick={showBasket(basketUrl)}
          >
            <SvgIcon icon="icon-shopping-bag" className="designer-projects-customer_stat_icon" />
            {basketCount > 0 && (
              <span className="designer-projects-customer_badge">
                {basketCount > 9 ? '9+' : basketCount}
              </span>
            )}
          </a>
        ) : (
          <span className="designer-projects-customer_stat_icon-slot">
            <SvgIcon icon="icon-shopping-bag" className="designer-projects-customer_stat_icon" />
          </span>
        )}
        {/* Orders on the label line and items under it, the same two lines as
            the date under Messages and the count under Favourites. */}
        {shopped ? (
          <Fragment>
            <a
              className="designer-projects-customer_stat_label -shopped"
              href={shopping.orders_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open this customer's orders"
              onClick={event => event.stopPropagation()}
            >
              {`Shopped ${plural(shopping.orders_count, 'order')}`}
            </a>
            <span className="designer-projects-customer_stat_value">
              {plural(shopping.items_count, 'item')}
            </span>
          </Fragment>
        ) : (
          <span className="designer-projects-customer_stat_label -not-shopped">
            Not shopped yet
          </span>
        )}
      </div>
    );
  };

  // The customer's unspent credit, on the line under their address.
  const renderCredit = (shopping, vouchersUrl) => {
    if (!shopping.credit_total_text) {
      return null;
    }
    const text = `${shopping.credit_total_text} shopping credit`;
    return vouchersUrl ? (
      <a
        className="designer-projects-customer_credit"
        href={vouchersUrl}
        title="See this customer's shopping credits"
        onClick={showVouchers(vouchersUrl)}
      >
        {text}
      </a>
    ) : (
      <div className="designer-projects-customer_credit">{text}</div>
    );
  };

  // When anyone last wrote to or from this customer, in whichever chat, so a
  // designer can see at a glance whether the conversation has gone quiet. ISO
  // strings of one format compare correctly as text.
  const lastMessageOn = chat => {
    const chats = ((chat && chat.chats) || []).filter(item => item.last_message_on_iso);
    if (chats.length === 0) {
      return null;
    }
    return chats.reduce(
      (latest, item) => (item.last_message_on_iso > latest.last_message_on_iso ? item : latest),
    ).last_message_on;
  };

  // Whatever date the project's current stage is actually about. Archived rooms
  // carry both ends of their life; the rest get the one that explains the status
  // line above them.
  const projectDateLines = project => {
    if (projectArchivedForCustomer(project)) {
      return [
        project.approved_at_date && `Design sent: ${project.approved_at_date}`,
        project.archived_at_date && `Archived: ${project.archived_at_date}`,
      ].filter(Boolean);
    }
    if (projectApproved(project)) {
      return [project.approved_at_date && `Design sent: ${project.approved_at_date}`].filter(
        Boolean,
      );
    }
    if (projectInProgress(project) || project.status === 'sent_to_review') {
      return [project.start_at_date && `Design started: ${project.start_at_date}`].filter(Boolean);
    }
    return [project.created_on_date && `Created: ${project.created_on_date}`].filter(Boolean);
  };

  // Real links, not click handlers, so the photo and the room text both open
  // the design and still take a middle-click or Cmd-click like any other link.
  const projectUrl = project => `/design/projects/${project.id}`;

  const removeProjectFromList = (project, customerId) => {
    const customerIdx = projectsByCustomer.findIndex(
      customerI => customerI.customer.id === customerId,
    );
    const customerProjects = projectsByCustomer[customerIdx];
    const index = customerProjects.projects.findIndex(projectI => projectI === project);
    if (index !== -1) {
      customerProjects.projects.splice(index, 1);
    }
    if (customerProjects.projects.length === 0) {
      projectsByCustomer.splice(customerIdx, 1);
    }
    setProjectsByCustomer([...projectsByCustomer]);
  };

  const archiveProject = (project, customerId) => {
    const msg = 'Do you really want to ARCHIVE this project?';
    const confirmation = confirm(msg);
    if (confirmation) {
      request('post', `/design/projects/${project.id}/archive.json`, {})
        .then(() => {
          removeProjectFromList(project, customerId);
        })
        .catch(() => {
          alert('Something went wrong. Please, try again.');
        });
    }
    return false;
  };

  const onArchiveProjectClick = (project, customerId) => event => {
    event.preventDefault();
    event.stopPropagation();
    archiveProject(project, customerId);
  };

  // The page pages by project, not by customer, and the grouping happens per
  // page, so one customer can come back on several pages - not only either side
  // of a boundary, since the list is ordered by date and their projects can sit
  // anywhere in it. Fold an incoming group into the one already on screen for
  // that customer, or their name and a second chat store appear twice.
  const appendGroups = incoming => {
    const merged = projectsByCustomer.slice();
    const indexByCustomer = {};
    merged.forEach((group, index) => {
      indexByCustomer[group.customer.id] = index;
    });

    incoming.forEach(group => {
      const index = indexByCustomer[group.customer.id];
      if (index === undefined) {
        indexByCustomer[group.customer.id] = merged.length;
        merged.push(group);
      } else {
        merged[index] = {
          ...merged[index],
          projects: merged[index].projects.concat(group.projects),
        };
      }
    });

    setProjectsByCustomer(merged);
  };

  const onLoadMoreClick = () => {
    if (loadingMore) {
      return;
    }
    const seq = searchSeq.current;
    setLoadingMore(true);
    request('get', nextLink)
      .then(data => {
        if (seq !== searchSeq.current) {
          return;
        }
        setNextLink(data.loadMoreLink);
        appendGroups(data.projectsByCustomer);
      })
      .catch(() => {
        alert('Something went wrong. Please, try again.');
      })
      .then(() => setLoadingMore(false));
  };

  // Filters as you type: the server searches, since the list is paged and the
  // customer may not be loaded yet. The address bar and the filter tabs follow
  // the query, so a reload or a switch to another list keeps the search.
  const runSearch = value => {
    searchSeq.current += 1;
    const seq = searchSeq.current;
    const q = value.trim();
    setQuery(q);

    const params = q ? { filter, q } : { filter };
    const pageUrl = `${window.location.pathname}?${new URLSearchParams(params)}`;
    window.history.replaceState(window.history.state, '', pageUrl);
    document.querySelectorAll('.designer-projects-filters_link').forEach(link => {
      const url = new URL(link.href);
      if (q) {
        url.searchParams.set('q', q);
      } else {
        url.searchParams.delete('q');
      }
      link.setAttribute('href', url.toString());
    });

    request('get', `${window.location.pathname}.json`, { params })
      .then(data => {
        if (seq !== searchSeq.current) {
          return;
        }
        setProjectsByCustomer(data.projectsByCustomer);
        setNextLink(data.loadMoreLink);
        setLoadingMore(false);
      })
      .catch(() => {
        if (seq === searchSeq.current) {
          alert('Something went wrong. Please, try again.');
        }
      });
  };

  // runSearch goes through a ref so the listeners, bound once, always call the
  // current render's copy.
  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  useEffect(() => {
    const form = document.getElementById(SEARCH_FORM_ID);
    const input = form && form.querySelector('input[name="q"]');
    const clear = form && form.querySelector('.designer-projects-search_clear');
    if (!input) {
      return undefined;
    }
    let timer = null;
    // The cross only shows while there is something to clear.
    const syncClear = () => {
      if (clear) {
        clear.hidden = input.value === '';
      }
    };
    const onInput = () => {
      syncClear();
      clearTimeout(timer);
      timer = setTimeout(() => runSearchRef.current(input.value), SEARCH_DELAY_MS);
    };
    const onClear = event => {
      event.preventDefault();
      clearTimeout(timer);
      input.value = '';
      syncClear();
      input.focus();
      runSearchRef.current('');
    };
    const onSubmit = event => {
      event.preventDefault();
      clearTimeout(timer);
      runSearchRef.current(input.value);
    };
    syncClear();
    input.addEventListener('input', onInput);
    form.addEventListener('submit', onSubmit);
    if (clear) {
      clear.addEventListener('click', onClear);
    }
    return () => {
      clearTimeout(timer);
      input.removeEventListener('input', onInput);
      form.removeEventListener('submit', onSubmit);
      if (clear) {
        clear.removeEventListener('click', onClear);
      }
    };
  }, []);

  const removeCustomerFromList = customerId => {
    setProjectsByCustomer(groups => groups.filter(group => group.customer.id !== customerId));
  };

  // The filter buttons live in the page header (my_projects.slim); a pin moves
  // one customer in or out of the Pinned list, so its count follows.
  const shiftPinnedCount = delta => {
    const count = document.querySelector('[data-filter-count="pinned"]');
    const current = count && parseInt(count.textContent.replace(/\D/g, ''), 10);
    if (count && !Number.isNaN(current)) {
      count.textContent = `(${Math.max(current + delta, 0)})`;
    }
  };

  const setCustomerPinned = (customerId, pinned) => {
    setProjectsByCustomer(groups =>
      groups.map(group => (group.customer.id === customerId ? { ...group, pinned } : group)),
    );
  };

  // A pin belongs to the customer: the server pins every project this designer
  // has for them, whichever one the request names.
  const onPinClick = group => event => {
    event.preventDefault();
    event.stopPropagation();
    const customerId = group.customer.id;
    if (pinningIds.indexOf(customerId) !== -1 || group.projects.length === 0) {
      return;
    }
    const pinned = !group.pinned;
    setPinningIds(ids => ids.concat(customerId));
    request('post', `/design/projects/${group.projects[0].id}/${pinned ? 'pin' : 'unpin'}`, {})
      .then(() => {
        shiftPinnedCount(pinned ? 1 : -1);
        // Unpinning from the pinned list takes the customer out of it; anywhere
        // else they stay and only the pin changes.
        if (!pinned && filter === 'pinned') {
          removeCustomerFromList(customerId);
        } else {
          setCustomerPinned(customerId, pinned);
        }
      })
      .catch(() => {
        alert('Something went wrong. Please, try again.');
      })
      .then(() => setPinningIds(ids => ids.filter(id => id !== customerId)));
  };

  const toggleCollapsed = customerId => {
    setCollapsedIds(
      ids =>
        ids.indexOf(customerId) === -1
          ? ids.concat(customerId)
          : ids.filter(id => id !== customerId),
    );
  };

  // Icon, what it is, then its value: the same three rows as Messages and shopping.
  const renderCustomerStat = (icon, label, value) => (
    <div className="designer-projects-customer_stat">
      <span className="designer-projects-customer_stat_icon-slot">
        <SvgIcon icon={icon} className="designer-projects-customer_stat_icon" />
      </span>
      <span className="designer-projects-customer_stat_label">{label}</span>
      <b className="designer-projects-customer_stat_value">{value}</b>
    </div>
  );

  // The separator above each customer's rooms: pin, who and where they are,
  // their chat, what they have favourited, their credit and whether they have
  // shopped, and a toggle that folds their rooms away.
  const renderCustomerHeader = group => {
    const customerId = group.customer.id;
    const shopping = group.shopping || EMPTY_SHOPPING;
    const collapsed = collapsedIds.indexOf(customerId) !== -1;
    const lastMessage = lastMessageOn(group.chat);
    return (
      <div
        className="designer-projects-customer_header"
        onClick={() => toggleCollapsed(customerId)}
      >
        <button
          type="button"
          className={cn('designer-projects-customer_pin', { '-pinned': group.pinned })}
          // A native title, not data-tooltip: the CSS tooltip is clipped by the
          // card's rounded overflow and by the list's own overflow: hidden.
          title={group.pinned ? 'Unpin Customer' : 'Pin Customer'}
          aria-label={group.pinned ? 'Unpin Customer' : 'Pin Customer'}
          disabled={pinningIds.indexOf(customerId) !== -1}
          onClick={onPinClick(group)}
        >
          <SvgIcon icon="icon-pin" />
        </button>
        <div className="designer-projects-customer_who">
          <div className="designer-projects-customer_name">{group.customer.full_name}</div>
          {group.address && (
            <div className="designer-projects-customer_address">{group.address}</div>
          )}
          {renderCredit(shopping, group.discount_vouchers_url)}
        </div>
        <div className="designer-projects-customer_stats">
          {/* The chat button keeps its own unread badge and opens the popup;
              clicks on it must not fold the customer away. */}
          <div
            className="designer-projects-customer_stat -chat"
            onClick={event => event.stopPropagation()}
          >
            <div className="designer-projects-customer_stat_icon-slot designer-projects-customer_chat">
              <Provider store={getChatStore(group.chat, customerId)}>
                <Chat />
              </Provider>
            </div>
            <span className="designer-projects-customer_stat_label">Messages</span>
            {lastMessage && (
              <span className="designer-projects-customer_stat_value">{lastMessage}</span>
            )}
          </div>
          {renderCustomerStat('icon-heart-empty', 'Favourites', shopping.favourites_count)}
          {renderShoppingStat(shopping, group.customer_basket_url, basketCounts[customerId])}
        </div>
        <button
          type="button"
          className={cn('designer-projects-customer_toggle', { '-collapsed': collapsed })}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Show projects' : 'Hide projects'}
        >
          <SvgIcon icon="icon-arrow-angle-down" />
        </button>
      </div>
    );
  };

  return (
    <div className="designer-projects-list">
      <DiscountVouchersDialog
        visible={vouchers.visible}
        loading={vouchers.loading}
        discountVouchers={vouchers.data}
        close={closeVouchers}
      />
      <CustomerBasketDialog
        visible={basket.visible}
        loading={basket.loading}
        lineItemsGroups={basket.data}
        errors={basket.errors}
        close={closeBasket}
        show={() => {}}
      />
      {projectsByCustomer.length === 0 && (
        <div className="designer-projects-list-items">
          <div className="designer-projects-list_item -nothing-found">
            <div>
              {query ? `No customers match “${query}”.` : EMPTY_COPY[filter] || EMPTY_COPY.active}
            </div>
          {/*  add a book free call */}
          </div>
        </div>
      )}
      {projectsByCustomer.map(projectsByCustomerItem => (
        <div
          key={projectsByCustomerItem.customer.id}
          className={cn('designer-projects-customer', {
            '-pinned': projectsByCustomerItem.pinned,
            '-collapsed': collapsedIds.indexOf(projectsByCustomerItem.customer.id) !== -1,
          })}
        >
          {renderCustomerHeader(projectsByCustomerItem)}
          <div className="designer-projects-customer_body">
            <div className="designer-projects-list-items">
              {projectsByCustomerItem.projects.map(project => (
                <div className="designer-projects-list_item" key={project.id}>
                  <a
                    className="project-preview designer-projects-list_item_photo"
                    href={projectUrl(project)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="project-preview_photo-wrapper">
                      {renderProjectImage(project)}
                    </div>
                    {project.can_archive && (
                      <button
                        type="button"
                        className="project-preview_unarchive"
                        data-tooltip="Archive Project"
                        onClick={onArchiveProjectClick(project, projectsByCustomerItem.customer.id)}
                      >
                        <SvgIcon icon="icon-archive" />
                      </button>
                    )}
                  </a>
                  <a
                    className="designer-projects-list_item_room"
                    href={projectUrl(project)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <h2 className="designer-projects-list_item_room-type">
                      {project.room_type_text}
                    </h2>
                    {project.service_text !== project.room_type_text && (
                      <div className="designer-projects-list_item_service">
                        {project.service_text}
                      </div>
                    )}
                    {project.term === 'express' && (
                      <div className="designer-projects-badge -express">
                        <SvgIcon icon="icon-clock" />
                        Express
                      </div>
                    )}
                    {projectDateLines(project).length > 0 && (
                      <div className="project-preview_dates">
                        {projectDateLines(project).map(line => (
                          <div key={line}>{line}</div>
                        ))}
                      </div>
                    )}
                  </a>
                  <div className="designer-projects-list_item_info">
                    {/* Status first, then the timeline: plain lines like the hint,
                        the two never both render for one project. */}
                    {renderTimelineHint(project)}
                    {renderStatusDescription(project)}
                    {isConsultancy(project) ? null : (
                      <div className="designer-projects-timeline">
                        <DesignStatus
                          project={project}
                          /* clickable while the design is in progress and after it is
                             approved: the design review and the shopping hand-off are
                             ticked by hand once the design is released (261005.1, 261005.2) */
                          editable={
                            (projectInProgress(project) || projectApproved(project)) &&
                            !projectMoreTimeApproved(project)
                          }
                          info={false}
                          statusHeader={false}
                          /* the serializer already sends design_process_status, so skip
                             the per-card fetch; a click saves and keeps its own copy */
                          fetchStatus={false}
                          /* project.customer and shoppingFormUser come only for
                             MBR designers and admins (shopping_form_visible?) */
                          shoppingFormUrl={
                            shoppingFormUser && project.customer
                              ? shoppingInfoFormUrl(project, shoppingFormUser)
                              : null
                          }
                          className="-inline"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      {nextLink && (
        <div className="designer-projects-list_load-more">
          <button type="button" className="btn -gold -y2023" disabled={loadingMore} onClick={onLoadMoreClick}>
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

DesignerProjectsList.propTypes = {
  projectsByCustomer: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filter: PropTypes.string,
  query: PropTypes.string,
  loadMoreLink: PropTypes.string,
  shoppingFormUser: PropTypes.shape({ full_name: PropTypes.string }),
};

DesignerProjectsList.defaultProps = {
  filter: 'active',
  query: '',
  loadMoreLink: null,
  shoppingFormUser: null,
};



// WEBPACK FOOTER //
// ./app/javascript/components/designer_projects_list/index.js